import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import { Card, Dropdown, DropdownButton } from "react-bootstrap";
import type { Options } from "highcharts";

import {
  getCaseSummary,
  CaseSummaryItem,
} from "../../../../../services/ActionStatusApi";
import AccordionComponent from "../../../../../common/Accordion";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartExportButton from "../../../../../common/HighchartExportButton";

const STAGE_ORDER = [
  "Cancelled",
  "Case Closed",
  "Report Finalized",
  "Report Review",
  "Drafting Report",
  "Evaluation",
  "Document Preparation",
  "Scheduled",
  "Scheduling",
  "Registered",
  "New",
];

interface CaseSummaryChartHighProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const CaseSummaryChartHigh: React.FC<CaseSummaryChartHighProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [chartData, setChartData] = useState<{ name: string; y: number }[]>([]);
  const [interval, setInterval] = useState<string>("1");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");
  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const barColor = "#21d198";

  useEffect(() => {
    initHighchartsModules();
  }, []);

  useEffect(() => {
    fetchSummaryData();
  }, [interval]);

  const fetchSummaryData = async () => {
    try {
      const result: CaseSummaryItem[] = await getCaseSummary(interval);

      const dataMap = new Map(
        result.map((item) => [item.stageName, item.countOfCases]),
      );

      const transformed = STAGE_ORDER.map((stage) => ({
        name: stage,
        y: dataMap.get(stage) || 0,
      }));

      setChartData(transformed);
    } catch (error) {
      console.error("Failed to load case summary data", error);
    }
  };

  const handleIntervalChange = (
    intervalValue: string | number,
    label: string,
  ) => {
    setInterval(String(intervalValue));
    setIntervalLabel(label);
  };

  const options: Options = {
    chart: {
      type: "bar",
      reflow: true,
    },
    title: { text: undefined },
    xAxis: {
      categories: chartData.map((d) => d.name),
      title: { text: null },
      reversed: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: null,
        align: "high",
      },
      labels: {
        overflow: "justify",
      },
    },
    tooltip: {
      valueSuffix: " cases",
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
        },
        color: barColor,
        pointWidth: 18,
      },
    },
    legend: {
      enabled: false,
    },
    credits: { enabled: false },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          enabled: true, // Keep it enabled so it renders
          symbol: "menu",
        },
      },
    },

    exportData: {
      enabled: true,
    },

    series: [
      {
        type: "bar",
        name: "Cases",
        data: chartData,
      },
    ],
  };

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseSummaryKey === key) {
      return; // do nothing
    }
    setCaseSummaryKey(key || "0");
  };

  const subjectadressAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
      isDashboard={true}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    const closeAll = () => setOpenDropdown(null);
    window.addEventListener("closeAllDropdowns", closeAll);
    return () => window.removeEventListener("closeAllDropdowns", closeAll);
  }, []);

  return (
    <div>
      {subjectadressAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Case Summary</span>
              <div className="d-flex align-items-center gap-2">
                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  stopPropagation={true}
                  show={openDropdown === "summaryMonth"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "summaryMonth" : null)
                  }
                />
              </div>
              <HighchartExportButton chartRef={chartComponentRef} />
            </div>
          ),
          content: (
            <Card className="border-0">
              <Card.Body>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={options}
                  ref={chartComponentRef}
                />
              </Card.Body>
            </Card>
          ),
        },
      ])}
    </div>
  );
};

export default CaseSummaryChartHigh;
